import { CellValueChangedEvent, ColDef } from '@ag-grid-community/core';
import { first, Observable, of, tap } from 'rxjs';
import { ArticleProductDimension, ArticleProductRole } from '@digiwall/goliath-products/proxy';
import { DocCreateArticleSerialDto, DocLineUpdatedDto } from '@digiwall/goliath-documents/proxy';
import { NewGuid } from '@digiwall/core';
import { isNullOrEmpty, Rest } from '@abp/ng.core';
import { LabelSubContext } from '@digiwall/goliath-core/proxy';
import { SupplierDeliveryNoteLinesEditorComponent } from '../supplier-delivery-note-lines-editor.component';
import { StockLinePolicy } from '../../../../stock-docs/stock-doc-movements/stock-doc-movement-detail/stock-doc-movement-lines/stock-doc-movement-lines-policies/stock-doc-movement-line.policy';
import { FieldTypes } from '@digiwall/ag-grid';

export class SupplierDeliveryNoteEntryMovementPolicy implements StockLinePolicy {
  constructor(protected component: SupplierDeliveryNoteLinesEditorComponent) {}

  get addPurchaseData() {
    return true;
  }

  enhanceColDefs(colDefs: ColDef[]): ColDef[] {
    this.initEntrySerialColDefs(colDefs);
    this.initCallbacksColDefs(colDefs);

    const colFieldToRemove = [
      'discount',
      'supplierDiscount',
      'vatCodeId',
      'quantityReceived',
      'unitWeight',
      'orderedQuantity',
      'totalReceivedQuantity',
      'additionalCostPrice',
      'remainingQuantityToReceived',
      'additionalPercentage',
    ];
    colDefs = colDefs.filter(c => !colFieldToRemove.includes(c.field!));
    return colDefs;
  }

  private initEntrySerialColDefs(colDefs: ColDef<any, any>[]) {
    const codeField = colDefs.find(x => x.field == 'code');
    const originalSelector = codeField.cellEditorSelector;

    codeField.cellEditorSelector = selectorParams => {
      const res = originalSelector(selectorParams);
      if (!res || typeof res === 'string') return res as any;
      const next = {
        ...res,
        params: { ...(res.params ?? {}) },
      };
      next.params.lookupFn = (term: string) =>
        this.component.stockArticleService.getArticleStockManagedSelectionLookup(term, true);

      return next;
    };

    const serialField = colDefs.find(x => x.field === 'articleSerialId');
    serialField.cellEditor = null;
    serialField.cellEditorParams = null;
    Object.assign(serialField, {
      field: 'articleSerial',
      width: 250,
      headerName: this.component.stockLocalizationService.instant('Doc::StockDoc:ArticleSerial'),
      editable: params => !params.data.articleUsesAutoSerial,
      type: FieldTypes.String,
    });

    colDefs.find(x => x.field == 'length').editable = params =>
      params.data.dimension == ArticleProductDimension.Length ||
      params.data.dimension == ArticleProductDimension.Volume ||
      params.data.dimension == ArticleProductDimension.Surface;
    colDefs.find(x => x.field == 'width').editable = params =>
      params.data.dimension == ArticleProductDimension.Volume ||
      params.data.dimension == ArticleProductDimension.Surface;
    colDefs.find(x => x.field == 'thickness').editable = params =>
      params.data.dimension == ArticleProductDimension.Volume;

    colDefs.push({
      field: 'articleSerialNote',
      headerName: this.component.stockLocalizationService.instant('GoliathCore::Note'),
      editable: true,
      type: FieldTypes.String,
      hide: true,
    });
  }

  private initCallbacksColDefs(colDefs: ColDef<any, any>[]) {
    const lotField = colDefs.find(x => x.field == 'articleLotId');
    lotField.editable = params => params.data.articleProductRoleId == ArticleProductRole.Lot;
    lotField.cellEditorParams.callback = (lineId: string, id: string, item: any) => {
      this.onSelectedCellCallback(lineId, 'articleLot', item);
    };

    const value1Field = colDefs.find(x => x.field == 'articleRangeValue1Id');
    value1Field.editable = params => params.data.articleRange1Id != null;
    value1Field.cellEditorParams.callback = (lineId: string, id: string, item: any) => {
      this.onSelectedCellCallback(lineId, 'articleRangeValue1', item);
    };

    const value2Field = colDefs.find(x => x.field == 'articleRangeValue2Id');
    value2Field.editable = params => params.data.articleRange2Id != null;
    value2Field.cellEditorParams.callback = (lineId: string, id: string, item: any) => {
      this.onSelectedCellCallback(lineId, 'articleRangeValue2', item);
    };

    const stockLocationField = colDefs.find(x => x.field == 'stockLocationId');
    stockLocationField.editable = true;
    stockLocationField.cellEditorParams.callback = (lineId: string, id: string, item: any) => {
      this.onSelectedCellCallback(lineId, 'stockLocation', item);
    };
  }

  private onSelectedCellCallback(lineId: string, colName: string, selectedItem?: any) {
    const line = this.component.lineGrid.grid.rowData.filter(x => x.id == lineId)[0];
    line[`${colName}Id`] = selectedItem.id;
    line[colName] = selectedItem.name?.current ?? selectedItem.code;

    this.afterStockCellCallback();
  }

  onCellValueChanged(event: CellValueChangedEvent): Observable<any> | boolean {
    switch (event.column.getColId()) {
      case 'unitPrice':
        return this.component.stockDocService.setLineUnitPrice(
          this.component.document.id,
          this.component.document.concurrencyStamp,
          event.data.id,
          event.data.unitPrice,
        );
      case 'articleStockUnitPrice':
        return this.component.stockDocService.setLineStockUnitPrice(
          this.component.document.id,
          this.component.document.concurrencyStamp,
          event.data.id,
          event.data.articleStockUnitPrice,
          true,
        );
        break;
      case 'articleSerial':
        var line = this.getLastLine();
        if (line != null) {
          line.articleSerial = event.value;
          this.afterStockCellCallback();
        }

        break;
      case 'length':
        return this.afterStockCellCallback(true);
        break;
      case 'width':
        return this.afterStockCellCallback(true);
        break;
      case 'thickness':
        return this.afterStockCellCallback(true);
        break;
      case 'articleSerialNote':
        this.afterStockCellCallback(true);
        break;
      case 'articlePurchaseUnitPrice':
        return true;
      default:
        return false;
    }
  }

  getAddArticleLineFn(): (
    docId: string,
    order: number,
    lineFromId: string,
    config?: Partial<Rest.Config>,
  ) => Observable<DocLineUpdatedDto> {
    return this.component.stockDocService.addArticleLine;
  }

  beforeAddArticleLine(): Observable<DocLineUpdatedDto | null> {
    return this.tryToAddSerialEntry();
  }

  beforeSaveDoc(): Observable<DocLineUpdatedDto | null> {
    if (this.getLastLine().articleSerialId != null) {
      return of(null);
    }

    return this.tryToAddSerialEntry().pipe(
      first(),
      tap(res => {
        if (res) {
          this.component.updateLines(res);
          if (this.component.printLabel()) {
            this.printLabelData(res.updatedLines[0]);
          }
        }
      }),
    );
  }

  onLineAdded(): void {
    if (this.component.copyLineData()) this.copyLastLineData();
    if (this.component.printLabel()) this.printLabelData();
  }

  private _unsavedLine: any;
  onUnsavedLineChanged(response: DocLineUpdatedDto): DocLineUpdatedDto {
    // However we need to keep any unsaved select values (i.e. ranges, batch & location)
    if (this._unsavedLine != null) {
      const updatedLine = response.updatedLines[0] as any;
      if (updatedLine != null) {
        const fields = [
          'articleLotId',
          'articleRangeValue1Id',
          'articleRangeValue2Id',
          'stockLocationId',
        ];
        fields.forEach(x => {
          updatedLine[x] = this._unsavedLine[x];
          const labelField = x.substring(0, x.length - 2);
          updatedLine[labelField] = this._unsavedLine[labelField];
        });
      }
      this._unsavedLine = null;
    }
    return response;
  }

  public beforeSaveLine(): void {
    if (!this.canAddSerialEntry()) {
      // We need to keep any unsaved select values (i.e. ranges, batch & location)
      this._unsavedLine = this.getLastLine();
    }
  }

  private afterStockCellCallback(keepPurchaseUnitPrice: boolean = false): boolean | null {
    if (!this.canAddSerialEntry()) {
      // Ensure the quantity is consistent even when the line is not yet updated
      return true;
    }

    this.component.stockIsBusyAdding(true);
    this.tryToAddSerialEntry(keepPurchaseUnitPrice).subscribeOnce(
      res => {
        if (res) {
          this.component.updateLines(res);
        }
      },
      () => this.component.stockIsBusyAdding(false),
      () => this.component.stockIsBusyAdding(false),
    );
  }

  private tryToAddSerialEntry(
    keepPurchaseUnitPrice: boolean = false,
  ): Observable<DocLineUpdatedDto | null> {
    const line = this.getLastLine();

    if (!line) return of(null);

    const payload = {
      id: line.articleSerialId ?? NewGuid,
      serialNumber: line.articleSerial,
      articleLotId: line.articleLotId,
      value1Id: line.articleRangeValue1Id,
      value2Id: line.articleRangeValue2Id,
      width: line.width,
      length: line.length,
      thickness: line.thickness,
      note: line.articleSerialNote,
      keepPurchaseUnitPrice,
    } as DocCreateArticleSerialDto;

    return this.component.stockDocService.addArticleLineSerialEntry(
      this.component.document.id,
      line.id,
      line.articleId,
      line.stockLocationId,
      line.sourceLineId,
      payload,
    );
  }

  private canAddSerialEntry(): boolean {
    const line = this.getLastLine();
    if (!line) return true;

    const maybeRequiredFields = [
      'articleSerial',
      'articleLotId',
      'articleRangeValue1Id',
      'articleRangeValue2Id',
      'stockLocationId',
      'width',
      'length',
      'thickness',
    ];

    for (const field of maybeRequiredFields) {
      if (this.isFieldEditable(field, line)) {
        const fieldValue = line[field];
        const empty = isNullOrEmpty(fieldValue);
        if (empty) {
          return false;
        }
      }
    }
    return true;
  }

  private isFieldEditable(field: string, data: any): boolean {
    const col = this.component.stockColDefs?.find(c => c.field === field);

    if (!col) return false;

    const { editable } = col;
    if (typeof editable === 'boolean') return editable;
    if (typeof editable === 'function') {
      try {
        return !!editable({ data: data } as any);
      } catch {
        return false;
      }
    }
  }

  private copyLastLineData(): void {
    const rows = this.component.lineGrid.grid.rowData;
    const newLine = rows[rows.length - 1];
    const prevLine = rows[rows.length - 2];

    if (newLine && prevLine) {
      Object.keys(prevLine).forEach(key => {
        if (key !== 'id' && key !== 'articleSerialId' && key !== 'articleSerial') {
          (newLine as any)[key] = (prevLine as any)[key];
        }
      });
      this.component.onArticleSelected({
        line: newLine,
        selectedItem: { id: newLine.articleId },
      });
    }
    this.component.lineGrid.grid.grid.api.redrawRows();
  }

  private printLabelData(line: any = null): void {
    if (line == null) {
      const lines = this.component.lineGrid.grid.rowData.filter(
        x => !isNullOrEmpty(x.articleSerialId),
      );

      if (lines.length == 0) return;
      line = lines[lines.length - 1];
    }

    this.component.stockLabelPrinterHelper.printMultipleOrSubEntity(
      this.component.stockArticleService,
      line.articleId,
      [line.articleSerialId],
      LabelSubContext.ArticleProductSerial,
    );
  }

  private getLastLine() {
    return this.component.lineGrid.grid.rowData[this.component.lineGrid.grid.rowData.length - 1];
  }

  focusOnNewLine() {
    return !this.component.copyLineData();
  }
}
